import {useEffect} from 'react';
import {SupportedCountry} from 'bigdatr-style';
import {useHistory, useLocation} from 'react-router-dom';

const contextAwarePathPrefixes = [
    '/explore-creative',
    '/creative',
    '/explore-media-value',
    '/export',
    '/compliance',
    '/report',
    '/segment',
    '/keywords',
    '/search'
];

type LocationLike = {
    pathname: string;
    search: string;
};

type ContextParams = {
    currentTeamId: string;
    country: SupportedCountry;
};

export function isContextAwarePathname(pathname: string) {
    return contextAwarePathPrefixes.some(
        (pathPrefix) => pathname === pathPrefix || pathname.startsWith(`${pathPrefix}/`)
    );
}

export function getContextAwareSearch(search: string, context: ContextParams) {
    const params = new URLSearchParams(search);
    params.set('currentTeam', context.currentTeamId);
    params.set('country', context.country);

    const nextSearch = params.toString();
    return nextSearch ? `?${nextSearch}` : '';
}

export function getContextAwareLocation(location: LocationLike, context: ContextParams) {
    if (!isContextAwarePathname(location.pathname)) return null;

    const search = getContextAwareSearch(location.search, context);
    if (search === location.search) return null;

    return {
        ...location,
        search
    };
}

function getHistoryStateWithSilentTracking(state: unknown) {
    if (state != null && typeof state === 'object' && !Array.isArray(state)) {
        return {
            ...state,
            dontTrack: true
        };
    }

    return {dontTrack: true};
}

export default function ContextAwareUrlSync(props: ContextParams) {
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        const currentLocation = history.location;
        const nextLocation = getContextAwareLocation(currentLocation, props);
        if (!nextLocation) return;

        history.replace({
            ...currentLocation,
            search: nextLocation.search,
            state: getHistoryStateWithSilentTracking(currentLocation.state)
        });
    }, [
        history,
        location.key,
        location.pathname,
        location.search,
        props.currentTeamId,
        props.country
    ]);

    return null;
}
